import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { IconButton } from '@mui/material';
import PrintIcon from '../../assets/images/print.svg';
import CheckedIcon from '../../assets/images/checked.svg';
import UnCheckIcon from '../../assets/images/uncheck.svg';
import { fetchApi } from '../../services/api';

import './detail-reports.component.css';
import Header from "../../sharedcomponents/header/header";

const maskEmail = (email) => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain) return '*****';
    return `*****@${domain}`;
};

const ReportsPage = () => {
    const location = useLocation();
    const { eightDInfoId } = location.state || {};
    const [EightDInfoList, setEightDInfoList] = useState(null);
    const [D1List, setD1List] = useState([]);
    const [D2List, setD2List] = useState(null);
    const [D3List, setD3List] = useState(null);
    const [D4List, setD4List] = useState(null);
    const [D5List, setD5List] = useState(null);
    const [D6List, setD6List] = useState(null);
    const [D7List, setD7List] = useState(null);
    const [D8List, setD8List] = useState(null);
    const [open, setOpen] = useState(false);
    const [zoomedImage, setZoomedImage] = useState(null);
    const [openSection, setOpenSection] = useState("general");
    const [isPrinting, setIsPrinting] = useState(false);
    const printResolveRef = useRef(null);

    const fetchAllFormData = useCallback((skipLoader = false) => {
        if (!eightDInfoId) return Promise.resolve();
        const id = Number(eightDInfoId);
        const body = JSON.stringify({ EightDInfoId: id });
        const opts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, ...(skipLoader && { skipLoader: true }) };
        const extractSingle = (r) => {
            const d = r?.data;
            if (!d) return r || null;
            if (d.model) return Array.isArray(d.model) ? d.model[0] : d.model;
            if (d.data && typeof d.data === 'object' && !d.D2Id && !d.D3Id && !d.D4Id && !d.D5Id && !d.D6Id && !d.D7Id && !d.D8Id && !d.EightDInfoId) return Array.isArray(d.data) ? d.data[0] : d.data;
            return Array.isArray(d) ? d[0] : d;
        };
        const extractList = (r) => {
            const d = r?.data;
            if (!d) return r || [];
            if (d.model) return d.model;
            if (Array.isArray(d.data)) return d.data;
            return Array.isArray(d) ? d : [];
        };

        return Promise.all([
            fetchApi('/api/EightDHeader/Get8DHeader', opts)
                .then(r => { setEightDInfoList(extractSingle(r)); })
                .catch(() => {}),
            fetchApi('/api/D1/GetD1', opts)
                .then(r => { let d = extractList(r); if (d && Array.isArray(d)) d = [...d].sort((a, b) => (a.MemberRole === 'Originator' ? -1 : b.MemberRole === 'Originator' ? 1 : 0)); setD1List(d || []); })
                .catch(() => setD1List([])),
            fetchApi('/api/D2/GetD2', opts).then(r => { setD2List(extractSingle(r)); }).catch(() => {}),
            fetchApi('/api/D3/GetD3', opts).then(r => { setD3List(extractSingle(r)); }).catch(() => {}),
            fetchApi('/api/D4/GetD4', opts).then(r => { setD4List(extractSingle(r)); }).catch(() => {}),
            fetchApi('/api/D5/GetD5', opts).then(r => { setD5List(extractSingle(r)); }).catch(() => {}),
            fetchApi('/api/D6/GetD6', opts).then(r => { setD6List(extractSingle(r)); }).catch(() => {}),
            fetchApi('/api/D7/GetD7', opts).then(r => { setD7List(extractSingle(r)); }).catch(() => {}),
            fetchApi('/api/D8/GetD8', opts).then(r => { setD8List(extractSingle(r)); }).catch(() => {}),
        ]);
    }, [eightDInfoId]);

    useEffect(() => {
        fetchAllFormData();
    }, [fetchAllFormData]);

    // When isPrinting becomes true, trigger browser print then restore
    useEffect(() => {
        if (isPrinting) {
            // Small delay to let React render all sections
            const timer = setTimeout(() => {
                window.print();
                setIsPrinting(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isPrinting]);

    const fetchDData = useCallback((dNumber) => {
        if (!eightDInfoId) return;
        
        const apiMap = {
            1: { endpoint: '/api/D1/GetD1', setter: setD1List, transform: (data) => 
                data ? [...data].sort((a, b) => {
                    if (a.MemberRole === 'Originator') return -1;
                    if (b.MemberRole === 'Originator') return 1;
                    return 0;
                }) : []
            },
            2: { endpoint: '/api/D2/GetD2', setter: setD2List },
            3: { endpoint: '/api/D3/GetD3', setter: setD3List },
            4: { endpoint: '/api/D4/GetD4', setter: setD4List },
            5: { endpoint: '/api/D5/GetD5', setter: setD5List },
            6: { endpoint: '/api/D6/GetD6', setter: setD6List },
            7: { endpoint: '/api/D7/GetD7', setter: setD7List },
            8: { endpoint: '/api/D8/GetD8', setter: setD8List }
        };

        const config = apiMap[dNumber];
        if (!config) return;

        fetchApi(config.endpoint, {
            method: 'POST',
            body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) })
        })
            .then(response => {
                const raw = response?.data;
                if (config.transform) {
                    const list = raw?.model || (Array.isArray(raw?.data) ? raw.data : null) || (Array.isArray(raw) ? raw : []);
                    config.setter(config.transform(list));
                } else {
                    let d = raw;
                    if (d && d.model) d = d.model;
                    else if (d && d.data && typeof d.data === 'object' && !d.D2Id && !d.D3Id && !d.D4Id && !d.D5Id && !d.D6Id && !d.D7Id && !d.D8Id && !d.EightDInfoId) d = d.data;
                    if (Array.isArray(d)) d = d[0];
                    config.setter(d || null);
                }
            })
            .catch(error => {
                config.setter(dNumber === 1 ? [] : null);
            });
    }, [eightDInfoId]);
    const get = (service, method, list, id) => {
        // implement your fetch/axios logic here
    };

    const isImage = (name, data) => {
        if (data && data.startsWith('data:image')) return true;
        if (!name) return false;
        return /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(name);
    };

    const renderAttachment = (attachment, i) => {
        const data = attachment.AttachmentData || attachment.attachmentData;
        const name = attachment.AttachmentName || attachment.attachmentName;
        if (isImage(name, data)) {
            return (
                <div key={i} style={{ marginBottom: '8px' }}>
                    <img
                        src={data}
                        alt={name}
                        style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', cursor: 'zoom-in' }}
                        onClick={() => setZoomedImage({ src: data, name })}
                    />
                </div>
            );
        }
        return (
            <div key={i} style={{ marginBottom: '8px' }}>
                <a
                    href={data || '#'}
                    download={name}
                    className="chip"
                    style={{ cursor: 'pointer', textDecoration: 'none' }}
                    onClick={!data ? (e) => e.preventDefault() : undefined}
                >
                    {name}
                </a>
            </div>
        );
    };
    const handlePrint = () => {
        // Fetch all data (skip loader), then show all sections and print
        fetchAllFormData(true).then(() => {
            setIsPrinting(true);
        });
    };
    // 🔑 Config object maps ids to service/method/list
    const sectionConfig = {
        general: { service: "eightdinfoService", method: "getEightDInfo", list: "EightDInfoList" },
        d1: { service: "d1Service", method: "getD1", list: "D1List" },
        d2: { service: "d2Service", method: "getD2", list: "D2List" },
        d3: { service: "d3Service", method: "getD3", list: "D3List" },
        d4: { service: "d4Service", method: "getD4", list: "D4List" },
        d5: { service: "d5Service", method: "getD5", list: "D5List" },
        d6: { service: "d6Service", method: "getD6", list: "D6List" },
        d7: { service: "d7Service", method: "getD7", list: "D7List" },
        d8: { service: "d8Service", method: "getD8", list: "D8List" },
    };
    const handleNavClick = (e) => {
        e.preventDefault();
        const id = e.currentTarget.id; // "general", "d1", "d2", …
        const cfg = sectionConfig[id];
        if (cfg && typeof get === "function") {
            get(cfg.service, cfg.method, cfg.list, id);
        }
        setOpenSection(id);
    };
    const anchorClick = useCallback((e, section) => {
        e.preventDefault();
        setOpen((prev) => !prev);
        
        const dNumber = section?.match(/d(\d)/)?.[1];
        if (dNumber) {
            fetchDData(Number(dNumber));
        }
    }, [fetchDData]);

    return (
        <Header>
            {zoomedImage && (
                <div className="img-lightbox-overlay" onClick={() => setZoomedImage(null)}>
                    <img src={zoomedImage.src} alt={zoomedImage.name} className="img-lightbox-content" onClick={(e) => e.stopPropagation()} />
                    <button className="img-lightbox-close" onClick={() => setZoomedImage(null)}>&#x2715;</button>
                </div>
            )}
            <div className="contentSection">
                <div className="mainContent">
                    <div className="PageTitle">
                        <span>All 8D Reports</span>
                        <div className="addNew">
                            <IconButton onClick={handlePrint} className="mat-icon">
                                <img
                                    src={PrintIcon}
                                    alt="Print icon"
                                    className="mat-icon material-icons mat-icon-no-color"
                                    role="img"
                                    aria-hidden="true"
                                />
                            </IconButton>
                        </div>
                    </div>

                    <div className={`detailReport${isPrinting ? '' : ' detailReport--flex'}`}>
                        <div className="col-10 float-left tabsList">
                            <div className="tab-content" id="myTabContent">
                                {/*General information */}
                                <div
                                    className={`tab-pane fade ${(isPrinting || openSection === "general") ? "show active" : ""}`}
                                    id="general"
                                >
                                    <h3>
                                        <a
                                            onClick={anchorClick}
                                            aria-controls="generalnfo"
                                            aria-expanded={open}
                                        >
                                            <span></span>
                                            General Information
                                        </a>
                                    </h3>

                                    <div className="detailsRow row">
                                        <div className="col-12 col-sm-6 float-left ReportNo">
                                            <h4>8D Report No.</h4>
                                            <p>{EightDInfoList?.ReportNo}</p>
                                        </div>
                                        <div className="col-12 col-sm-6 float-left">
                                            <h4>Division</h4>
                                            <p>{EightDInfoList?.Division}</p>
                                        </div>
                                        <div className="clearfix"></div>
                                    </div>

                                    <div className="detailsRow row">
                                        <div className="col-12 col-sm-6 float-left">
                                            <h4>Problem Statement</h4>
                                            <p>{EightDInfoList?.ProblemStatement}</p>
                                        </div>
                                        <div className="col-12 col-sm-6 float-left">
                                            <h4>Start Date</h4>
                                            <p>
                                                {EightDInfoList?.StartDate
                                                    ? new Date(EightDInfoList.StartDate).toLocaleDateString('en-GB')
                                                    : ''}
                                            </p>
                                        </div>
                                        <div className="clearfix"></div>
                                    </div>

                                    <div className="detailsRow row">
                                        <div className="col-12 col-sm-6 float-left">
                                            <h4>Part Description</h4>
                                            <p>{EightDInfoList?.PartDescription}</p>
                                        </div>
                                        <div className="col-12 col-sm-6 float-left">
                                            <h4>Part Number</h4>
                                            <p>{EightDInfoList?.PartNo}</p>
                                        </div>
                                        <div className="clearfix"></div>
                                    </div>

                                    <div className="detailsRow row">
                                        <div className="col-12 col-sm-6 float-left">
                                            <h4>Supplier Name</h4>
                                            <p>{EightDInfoList?.SupplierName}</p>
                                        </div>
                                        <div className="col-12 col-sm-6 float-left">
                                            <h4>Source of Complain</h4>
                                            <p>{EightDInfoList?.SourceOfComplain}</p>
                                        </div>
                                        <div className="clearfix"></div>
                                    </div>

                                    <div className="detailsRow row">
                                        <div className="col-12 col-sm-6 float-left">
                                            <h4>Severity (Points)</h4>
                                            <p>{EightDInfoList?.SeverityPoints}</p>
                                        </div>
                                        <div className="col-12 col-sm-6 float-left checkItem">
                                            {EightDInfoList?.IsRecurringIssue ? (
                                                <img src={CheckedIcon} alt="checked" />
                                            ) : (
                                                <img src={UnCheckIcon} alt="unchecked" />
                                            )}
                                            Recurring Issue?
                                        </div>
                                        <div className="clearfix"></div>
                                    </div>

                                    <div className="detailsRow row">
                                        <div className="col-12 col-sm-6 float-left checkItem">
                                            {EightDInfoList?.IsCustomerSafetyEffect ? (
                                                <img src={CheckedIcon} alt="checked" />
                                            ) : (
                                                <img src={UnCheckIcon} alt="unchecked" />
                                            )}
                                            Customer Safety Effect
                                        </div>
                                        <div className="clearfix"></div>
                                    </div>


                                </div>

                                {/*D1 information */}
                                <div className="pagebreak"> </div>

                                <div
                                    className={`tab-pane fade ${(isPrinting || openSection === "d1") ? "show active" : ""}`}
                                    id="d1"
                                >
                                    <h3>
                                        <a
                                            onClick={(e) => anchorClick(e, 'd1')}
                                            aria-controls="generalnfo"
                                            aria-expanded={open}
                                        >
                                            <span></span>
                                            D1 : Use a Team Approach
                                        </a>
                                    </h3>
                                    <p className="formInfo">
                                        The Problem Solving Investigator uses a multi-person approach to collect
                                        information from those directly associated with and/or impacted by the
                                        problem. The Team members names and contact info should be listed here.
                                    </p>

                                    {/* {D1List.length > 0 && ( */}

                                    <table className="table customTables">
                                        <thead>
                                            <tr>
                                                <th>Team Member Name</th>
                                                <th>Email ID</th>
                                                <th>Mobile No.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {D1List.map((d1, i) => (
                                                <tr key={i}>
                                                    <td data-label="Team Member Name">
                                                        {d1?.TeamMemberName}
                                                        {d1?.MemberRole === 'Originator' && <span style={{ marginLeft: '8px', color: '#810055' }}>(O)</span>}
                                                        {d1?.MemberRole === 'Contributor' && <span style={{ marginLeft: '8px', color: '#666' }}>(C)</span>}
                                                    </td>
                                                    <td data-label="Email ID">{maskEmail(d1?.EmailId)}</td>
                                                    <td data-label="Mobile No.">{d1?.MobileNo}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/*D2 information */}
                                <div className="pagebreak"> </div>

                                <div
                                    className={`tab-pane fade ${(isPrinting || openSection === "d2") ? "show active" : ""}`}
                                    id="d2"
                                    role="tabpanel"
                                    aria-labelledby="contact-tab"
                                >
                                    <h3>
                                        <a
                                            onClick={(e) => anchorClick(e, 'd2')}
                                            data-toggle="collapse"
                                            data-parent=".tab-pane"
                                            href="#form2"
                                            aria-controls="form2"
                                            aria-expanded={open}
                                        >
                                            <span></span>
                                            D2 : Describe the Problem
                                        </a>
                                    </h3>

                                    <p className="formInfo">
                                        Collect the data from those involved with the problem and produce a Well Formed Description.
                                        WHAT is the problem in comparison to a conforming part?
                                    </p>

                                    <div className="reportsDetails" id="form2">
                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-6 float-left">
                                                <h4>What is the problem in comparison to a conforming part?</h4>
                                                <p>{D2List?.WhatProblem || D2List?.whatProblem}</p>
                                            </div>
                                            <div className="col-12 col-sm-6 float-left">
                                                <h4>Why is it a problem?</h4>
                                                <p>{D2List?.WhyProblem || D2List?.whyProblem}</p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-6 float-left">
                                                <h4>When was the problem discovered?</h4>
                                                <p>
                                                    {(D2List?.WhenProblemDiscovered || D2List?.whenProblemDiscovered) &&
                                                        new Date(D2List.WhenProblemDiscovered || D2List.whenProblemDiscovered).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="col-12 col-sm-6 float-left">
                                                <h4>How was it detected?</h4>
                                                <p>{D2List?.HowProblemDetected || D2List?.howProblemDetected}</p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-6 float-left">
                                                <h4>How many defective parts have been detected</h4>
                                                {(D2List?.DefectivePartsDetected || D2List?.defectivePartsDetected)?.length > 0 && (
                                                    <table className="table customTables">
                                                        <thead>
                                                            <tr>
                                                                <th>Date</th>
                                                                <th>No. of defective</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(D2List.DefectivePartsDetected || D2List.defectivePartsDetected).map((defective, i) => (
                                                                <tr key={i}>
                                                                    <td>
                                                                        {(defective.DetectedDate || defective.detectedDate) &&
                                                                            new Date(defective.DetectedDate || defective.detectedDate).toLocaleDateString("en-GB")}
                                                                    </td>
                                                                    <td className="countClass">{defective.NoOfPartsDetected || defective.noOfPartsDetected}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-6 float-left">
                                                <h4>Comments</h4>
                                                <p>{D2List?.DefectivePartComment || D2List?.defectivePartComment}</p>
                                            </div>
                                            <div className="col-12 col-sm-6 float-left">
                                                <h4>Are other part number affected?</h4>
                                                {(D2List?.AreOtherPartNumberAffected || D2List?.areOtherPartNumberAffected) ? (
                                                    <p><span>Affected</span></p>
                                                ) : (
                                                    <p><span>Not Affected</span></p>
                                                )}
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-6 float-left">
                                                <h4>Reference?</h4>
                                                <p>{D2List?.Reference || D2List?.reference}</p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        {(D2List?.Attachments || D2List?.attachments)?.length > 0 && (
                                            <div className="detailsRow row reportdetailsPage">
                                                <div className="col-12 col-sm-12 float-left">
                                                    <h4>Attachments</h4>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                        {(D2List.Attachments || D2List.attachments).map(renderAttachment)}
                                                    </div>
                                                </div>
                                                <div className="clearfix"></div>
                                            </div>
                                        )}

                                        <div className="detailsRow row actionsPanel">
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Responsible Person</h4>
                                                <p>{D2List?.ResponsiblePerson || D2List?.responsiblePerson}</p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Planned Date</h4>
                                                <p>
                                                    {(D2List?.PlannedDate || D2List?.plannedDate) &&
                                                        new Date(D2List.PlannedDate || D2List.plannedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Achieved Date</h4>
                                                <p>
                                                    {(D2List?.AchievedDate || D2List?.achievedDate) &&
                                                        new Date(D2List.AchievedDate || D2List.achievedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>
                                    </div>
                                </div>

                                {/*D3 information */}
                                <div className="pagebreak"> </div>
                                <div
                                    className={`tab-pane fade ${(isPrinting || openSection === "d3") ? "show active" : ""}`}
                                    id="d3"
                                    role="tabpanel"
                                    aria-labelledby="contact-tab"
                                >
                                    <h3>
                                        <a
                                            onClick={(e) => anchorClick(e, 'd3')}
                                            data-toggle="collapse"
                                            data-parent=".tab-pane"
                                            href="#form3"
                                            aria-controls="form3"
                                            aria-expanded={open}
                                        >
                                            <span></span>
                                            D3 : Implement and Verify Containment (Temp Fix)
                                        </a>
                                    </h3>

                                    <p className="formInfo">
                                        Implement containment to control the situation and mitigate further negative effects of the problem.
                                        (segregation, selection, temporary actions in the process etc…..)
                                    </p>

                                    <div className="reportsDetails" id="form3">
                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-12">
                                                <h4>Write containment details?</h4>
                                                {(D3List?.ContainmentDetails || D3List?.containmentDetails)?.length > 0 ? (
                                                    <table className="table customTables">
                                                        <thead>
                                                            <tr>
                                                                <th>Containment</th>
                                                                <th>Action</th>
                                                                <th>Responsible for Action</th>
                                                                <th>Target Date</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(D3List.ContainmentDetails || D3List.containmentDetails).map((d3, i) => (
                                                                <tr key={i}>
                                                                    <td data-label="Containment">{d3?.Containment || d3?.containment}</td>
                                                                    <td data-label="Action">{d3?.Action || d3?.action}</td>
                                                                    <td data-label="Responsible for Action">{d3?.Responsible || d3?.responsible}</td>
                                                                    <td data-label="Target Date">
                                                                        {(d3?.TargetDate || d3?.targetDate) &&
                                                                            new Date(d3.TargetDate || d3.targetDate).toLocaleDateString("en-GB")}
                                                                    </td>
                                                                    <td data-label="Status">{d3?.Status || d3?.status}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <p style={{ color: '#999', fontStyle: 'italic', fontSize: '14px' }}>No containment details available</p>
                                                )}
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-6 float-left">
                                                <h4>Markings on delivered OK parts?</h4>
                                                {(D3List?.IsMarkedDeliveredOKParts || D3List?.isMarkedDeliveredOKParts) ? (
                                                    <p><span>Delivered</span></p>
                                                ) : (
                                                    <p><span>Not Delivered</span></p>
                                                )}
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-6 float-left">
                                                <h4>Sorting result at each step of the flow</h4>
                                            </div>
                                            {(D3List?.SortingResults || D3List?.sortingResults)?.length > 0 ? (
                                                <table className="table customTables">
                                                    <thead>
                                                        <tr>
                                                            <th>Location</th>
                                                            <th>Material Sorted</th>
                                                            <th>Quantities</th>
                                                            <th>Comments</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(D3List.SortingResults || D3List.sortingResults).map((d3, i) => (
                                                            <tr key={i}>
                                                                <td data-label="Location">{d3?.Location || d3?.location}</td>
                                                                <td data-label="Material Sorted">
                                                                    {(d3?.IsMaterialSorted || d3?.isMaterialSorted) ? (
                                                                        <p><span>Yes</span></p>
                                                                    ) : (
                                                                        <p><span>No</span></p>
                                                                    )}
                                                                </td>
                                                                <td data-label="Quantities">{d3?.Quantity || d3?.quantity}</td>
                                                                <td data-label="Comments">{d3?.Comment || d3?.comment}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <p style={{ color: '#999', fontStyle: 'italic', fontSize: '14px' }}>No sorting results available</p>
                                            )}
                                            <div className="clearfix"></div>
                                        </div>

                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-6 float-left">
                                                <h4>What we have learned from the sorting?</h4>
                                                <p>{D3List?.WhatLearnedFromSorting || D3List?.LearnedFromSorting || D3List?.whatLearnedFromSorting || D3List?.learnedFromSorting}</p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        {(D3List?.Attachments || D3List?.attachments)?.length > 0 && (
                                            <div className="detailsRow row reportdetailsPage">
                                                <div className="col-12 col-sm-12 float-left">
                                                    <h4>Attachments</h4>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                        {(D3List.Attachments || D3List.attachments).map(renderAttachment)}
                                                    </div>
                                                </div>
                                                <div className="clearfix"></div>
                                            </div>
                                        )}

                                        <div className="detailsRow row actionsPanel">
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Responsible Person</h4>
                                                <p>{D3List?.ResponsiblePerson || D3List?.responsiblePerson}</p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Planned Date</h4>
                                                <p>
                                                    {(D3List?.PlannedDate || D3List?.plannedDate) &&
                                                        new Date(D3List.PlannedDate || D3List.plannedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Achieved Date</h4>
                                                <p>
                                                    {(D3List?.AchievedDate || D3List?.achievedDate) &&
                                                        new Date(D3List.AchievedDate || D3List.achievedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>
                                    </div>
                                </div>

                                {/*D4 information */}
                                <div className="pagebreak"> </div>
                                <div
                                    className={`tab-pane fade ${(isPrinting || openSection === "d4") ? "show active" : ""}`}
                                    id="d4"
                                    role="tabpanel"
                                    aria-labelledby="contact-tab"
                                >
                                    <h3>
                                        <a
                                            onClick={(e) => anchorClick(e, 'd4')}
                                            data-toggle="collapse"
                                            data-parent=".tab-pane"
                                            href="#form4"
                                            aria-controls="form4"
                                            aria-expanded={open}
                                        >
                                            <span></span>
                                            D4 : Root Cause Analysis
                                        </a>
                                    </h3>

                                    <p className="formInfo">
                                        Identify the Possible Causes of the problem for both occurrence and non detection.
                                        Conduct a Root Cause Analysis to determine true Root Cause of the problem using the 5 why’s.
                                        Reproduce the defect to confirm the true root cause.
                                    </p>

                                    <div className="reportsDetails" id="form4">
                                        {/* Occurrence Section */}
                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-12">
                                                <h4>Occurrence</h4>
                                                {(D4List?.OccurrenceResult || D4List?.occurrenceResult)?.length > 0 ? (
                                                    <table className="table customTables">
                                                        <thead>
                                                            <tr>
                                                                <th>Sr. no.</th>
                                                                <th>Why’s</th>
                                                                <th>Attachment</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(D4List.OccurrenceResult || D4List.occurrenceResult).map((d4, i) => (
                                                                <tr key={i}>
                                                                    <td data-label="Sr. no.">{i + 1}</td>
                                                                    <td data-label="Why’s">{d4?.Why || d4?.why}</td>
                                                                    <td data-label="Attachment">
                                                                        {(d4?.ProofAttachmentName || d4?.proofAttachmentName) && (
                                                                            <span className="chip">
                                                                                {d4.ProofAttachmentName || d4.proofAttachmentName}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <p style={{ color: '#999', fontStyle: 'italic', fontSize: '14px' }}>No occurrence data available</p>
                                                )}
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        {/* Non Detection Section */}
                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-12">
                                                <h4>Non Detection</h4>
                                                {(D4List?.NonDetectionDetails || D4List?.nonDetectionDetails)?.length > 0 ? (
                                                    <table className="table customTables">
                                                        <thead>
                                                            <tr>
                                                                <th>Sr. no.</th>
                                                                <th>Why’s</th>
                                                                <th>Attachment</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(D4List.NonDetectionDetails || D4List.nonDetectionDetails).map((d4, i) => (
                                                                <tr key={i}>
                                                                    <td data-label="Sr. no.">{i + 1}</td>
                                                                    <td data-label="Why’s">{d4?.Why || d4?.why}</td>
                                                                    <td data-label="Attachment">
                                                                        {(d4?.ProofAttachmentName || d4?.proofAttachmentName) && (
                                                                            <span className="chip">
                                                                                {d4.ProofAttachmentName || d4.proofAttachmentName}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <p style={{ color: '#999', fontStyle: 'italic', fontSize: '14px' }}>No non-detection data available</p>
                                                )}
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        {/* Attachments */}
                                        {(D4List?.Attachments || D4List?.attachments)?.length > 0 && ( 
                                        <div className="detailsRow row reportdetailsPage">
                                             <div className="col-12 col-sm-12 float-left">
                                                    <h4>Attachments</h4>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                        {(D4List.Attachments || D4List.attachments).map(renderAttachment)}
                                                    </div>
                                                </div>
                                                <div className="clearfix"></div>
                                        </div>
                                         )}
                                        {/* Actions Panel */}
                                        <div className="detailsRow row actionsPanel">
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Responsible Person</h4>
                                                <p>{D4List?.ResponsiblePerson || D4List?.responsiblePerson}</p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Planned Date</h4>
                                                <p>
                                                    {(D4List?.PlannedDate || D4List?.plannedDate) &&
                                                        new Date(D4List.PlannedDate || D4List.plannedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Achieved Date</h4>
                                                <p>
                                                    {(D4List?.AchievedDate || D4List?.achievedDate) &&
                                                        new Date(D4List.AchievedDate || D4List.achievedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>
                                    </div>
                                </div>

                                {/*D5 information */}
                                <div className="pagebreak"> </div>
                                <div
                                    className={`tab-pane fade ${(isPrinting || openSection === "d5") ? "show active" : ""}`}
                                    id="d5"
                                    role="tabpanel"
                                    aria-labelledby="contact-tab"
                                >
                                    <h3>
                                        <a
                                            onClick={(e) => anchorClick(e, 'd5')}
                                            data-toggle="collapse"
                                            data-parent=".tab-pane"
                                            href="#form5"
                                            aria-controls="form5"
                                            aria-expanded={open}
                                        >
                                            <span></span>
                                            D5 : Develop Permanent Corrective Action/Solution
                                        </a>
                                    </h3>

                                    <p className="formInfo">
                                        Develop Permanent Solution(s) that address each Root Cause. Verify and test the Permanent Solution(s).
                                    </p>

                                    <div className="reportsDetails" id="form5">
                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-12">
                                                <h4>Occurrence</h4>
                                                {(D5List?.OccurrenceResult || D5List?.occurrenceResult)?.length > 0 && (
                                                <table className="table customTables">
                                                    <thead>
                                                        <tr>
                                                            <th>Root Cause</th>
                                                            <th>Action</th>
                                                            <th>Responsible for Action</th>
                                                            <th>Target Date</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(D5List.OccurrenceResult || D5List.occurrenceResult).map((d5, i) => (
                                                                <tr key={i}>
                                                                    <td data-label="Root Cause">{d5?.RootCause || d5?.rootCause}</td>
                                                                    <td data-label="Action">{d5?.Action || d5?.action}</td>
                                                                    <td data-label="Responsible for Action">{d5?.Responsible || d5?.responsible}</td>
                                                                    <td data-label="Target Date">
                                                                        {(d5?.TargetDate || d5?.targetDate) &&
                                                                            new Date(d5.TargetDate || d5.targetDate).toLocaleDateString("en-GB")}
                                                                    </td>
                                                                    <td data-label="Status">{d5?.Status || d5?.status}</td>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </table>
                                                )}
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-12">
                                                <h4>Non Detection</h4>
                                                {(D5List?.NonDetectionDetails || D5List?.nonDetectionDetails)?.length > 0 && (
                                                <table className="table customTables">
                                                    <thead>
                                                        <tr>
                                                            <th>Root Cause</th>
                                                            <th>Action</th>
                                                            <th>Responsible for Action</th>
                                                            <th>Target Date</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(D5List.NonDetectionDetails || D5List.nonDetectionDetails).map((d5, i) => (
                                                                <tr key={i}>
                                                                    <td data-label="Root Cause">{d5?.RootCause || d5?.rootCause}</td>
                                                                    <td data-label="Action">{d5?.Action || d5?.action}</td>
                                                                    <td data-label="Responsible for Action">{d5?.Responsible || d5?.responsible}</td>
                                                                    <td data-label="Target Date">
                                                                        {(d5?.TargetDate || d5?.targetDate) &&
                                                                            new Date(d5.TargetDate || d5.targetDate).toLocaleDateString("en-GB")}
                                                                    </td>
                                                                    <td data-label="Status">{d5?.Status || d5?.status}</td>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </table>
                                                )}
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        {(D5List?.Attachments || D5List?.attachments)?.length > 0 && (
                                        <div className="detailsRow row reportdetailsPage">
                                            <div className="col-12 col-sm-12 float-left">
                                                <h4>Attachments</h4>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                    {(D5List.Attachments || D5List.attachments).map(renderAttachment)}
                                                </div>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>
                                        )}

                                        <div className="detailsRow row actionsPanel">
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Responsible Person</h4>
                                                <p>{D5List?.ResponsiblePerson || D5List?.responsiblePerson}</p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Planned Date</h4>
                                                <p>
                                                    {(D5List?.PlannedDate || D5List?.plannedDate) &&
                                                        new Date(D5List.PlannedDate || D5List.plannedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Achieved Date</h4>
                                                <p>
                                                    {(D5List?.AchievedDate || D5List?.achievedDate) &&
                                                        new Date(D5List.AchievedDate || D5List.achievedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>
                                    </div>
                                </div>

                                {/*D6 information */}
                                <div className="pagebreak"> </div>
                                <div
                                    className={`tab-pane fade ${(isPrinting || openSection === "d6") ? "show active" : ""}`}
                                    id="d6"
                                    role="tabpanel"
                                    aria-labelledby="contact-tab"
                                >
                                    <h3>
                                        <a
                                            onClick={(e) => anchorClick(e, 'd6')}
                                            data-toggle="collapse"
                                            data-parent=".tab-pane"
                                            href="#form6"
                                            aria-controls="form6"
                                            aria-expanded={open}
                                        >
                                            <span></span>
                                            D6 : Validate Permanent CA/Solution
                                        </a>
                                    </h3>

                                    <p className="formInfo">Validate that the chosen solution is effective.</p>

                                    <div className="reportsDetails" id="form6">
                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-12 float-left">
                                                <h4>Permanent Solution Statement?</h4>
                                                <p>{D6List?.PermanentSolution || D6List?.permanentSolution}</p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        {(D6List?.Attachments || D6List?.attachments)?.length > 0 && (
                                            <div className="detailsRow row reportdetailsPage">
                                                <div className="col-12 col-sm-12 float-left">
                                                    <h4>Attachments</h4>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                        {(D6List.Attachments || D6List.attachments).map(renderAttachment)}
                                                    </div>
                                                </div>
                                                <div className="clearfix"></div>
                                            </div>
                                        )}

                                        <div className="detailsRow row actionsPanel">
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Responsible Person</h4>
                                                <p>{D6List?.ResponsiblePerson || D6List?.responsiblePerson}</p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Planned Date</h4>
                                                <p>
                                                    {(D6List?.PlannedDate || D6List?.plannedDate) &&
                                                        new Date(D6List.PlannedDate || D6List.plannedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Achieved Date</h4>
                                                <p>
                                                    {(D6List?.AchievedDate || D6List?.achievedDate) &&
                                                        new Date(D6List.AchievedDate || D6List.achievedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>
                                    </div>
                                </div>

                                {/*D7 information */}
                                <div className="pagebreak"> </div>
                                <div
                                    className={`tab-pane fade ${(isPrinting || openSection === "d7") ? "show active" : ""}`}
                                    id="d7"
                                    role="tabpanel"
                                    aria-labelledby="contact-tab"
                                >
                                    <h3>
                                        <a
                                            onClick={(e) => anchorClick(e, 'd7')}
                                            data-toggle="collapse"
                                            data-parent=".tab-pane"
                                            href="#form7"
                                            aria-controls="form7"
                                            aria-expanded={open}
                                        >
                                            <span></span>
                                            D7 : Prevent Recurrence
                                        </a>
                                    </h3>

                                    <p className="formInfo">
                                        Prevent recurrence of the problem at the problem site and across the organization.
                                        Standards update (PFMEA, control plan, work instructions, maintenance plan, procedures…)
                                    </p>

                                    <div className="reportsDetails" id="form7">
                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-12 float-left">
                                                <h4>Holistic View/Generalization</h4>
                                                <p>{D7List?.HolisticView || D7List?.holisticView}</p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-12 float-left">
                                                <h4>Write containment details?</h4>
                                            </div>
                                            {(D7List?.Action || D7List?.action)?.length > 0 && (
                                                <table className="table customTables">
                                                    <thead>
                                                        <tr>
                                                            <th>Action</th>
                                                            <th>Responsible for Action</th>
                                                            <th>Target Date</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(D7List.Action || D7List.action).map((d7, i) => (
                                                            <tr key={i}>
                                                                <td data-label="Action">{d7?.Action || d7?.action}</td>
                                                                <td data-label="Responsible for Action">{d7?.Responsible || d7?.responsible}</td>
                                                                <td data-label="Target Date">
                                                                    {(d7?.TargetDate || d7?.targetDate) &&
                                                                        new Date(d7.TargetDate || d7.targetDate).toLocaleDateString("en-GB")}
                                                                </td>
                                                                <td data-label="Status">{d7?.Status || d7?.status}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                            <div className="clearfix"></div>
                                        </div>

                                        {(D7List?.Attachments || D7List?.attachments)?.length > 0 && (
                                            <div className="detailsRow row reportdetailsPage">
                                                <div className="col-12 col-sm-12 float-left">
                                                    <h4>Attachments</h4>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                        {(D7List.Attachments || D7List.attachments).map(renderAttachment)}
                                                    </div>
                                                </div>
                                                <div className="clearfix"></div>
                                            </div>
                                        )}

                                        <div className="detailsRow row actionsPanel">
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Responsible Person</h4>
                                                <p>{D7List?.ResponsiblePerson || D7List?.responsiblePerson}</p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Planned Date</h4>
                                                <p>
                                                    {(D7List?.PlannedDate || D7List?.plannedDate) &&
                                                        new Date(D7List.PlannedDate || D7List.plannedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Achieved Date</h4>
                                                <p>
                                                    {(D7List?.AchievedDate || D7List?.achievedDate) &&
                                                        new Date(D7List.AchievedDate || D7List.achievedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>
                                    </div>
                                </div>

                                {/*D8 information */}
                                <div className="pagebreak"> </div>

                                <div
                                    className={`tab-pane fade ${(isPrinting || openSection === "d8") ? "show active" : ""}`}
                                    id="d8"
                                    role="tabpanel"
                                    aria-labelledby="contact-tab"
                                >
                                    <h3>
                                        <a
                                            onClick={(e) => anchorClick(e, 'd8')}
                                            data-toggle="collapse"
                                            data-parent=".tab-pane"
                                            href="#form8"
                                            aria-controls="form8"
                                            aria-expanded={open}
                                        >
                                            <span></span>
                                            D8 : Use a Team Approach
                                        </a>
                                    </h3>

                                    <p className="formInfo">
                                        Audit implemented solutions. (when, result) Reinforce the 8D process with recognition of team member contributions.
                                    </p>

                                    <div className="reportsDetails" id="form8">
                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-12 float-left">
                                                <h4>Appreciations Comments</h4>
                                                <p>{D8List?.AppreciationComment || D8List?.appreciationComment}</p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        <div className="detailsRow row">
                                            <div className="col-12 col-sm-12 float-left">
                                                <h4>CC List</h4>
                                                <p>{D8List?.CcList || D8List?.ccList}</p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>

                                        {(D8List?.Attachments || D8List?.attachments)?.length > 0 && (
                                            <div className="detailsRow row reportdetailsPage">
                                                <div className="col-12 col-sm-12 float-left">
                                                    <h4>Attachments</h4>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                        {(D8List.Attachments || D8List.attachments).map(renderAttachment)}
                                                    </div>
                                                </div>
                                                <div className="clearfix"></div>
                                            </div>
                                        )}

                                        <div className="detailsRow row actionsPanel">
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Responsible Person</h4>
                                                <p>{D8List?.ResponsiblePerson || D8List?.responsiblePerson}</p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Planned Date</h4>
                                                <p>
                                                    {(D8List?.PlannedDate || D8List?.plannedDate) &&
                                                        new Date(D8List.PlannedDate || D8List.plannedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="col-12 col-sm-4 float-left">
                                                <h4>Achieved Date</h4>
                                                <p>
                                                    {(D8List?.AchievedDate || D8List?.achievedDate) &&
                                                        new Date(D8List.AchievedDate || D8List.achievedDate).toLocaleDateString("en-GB")}
                                                </p>
                                            </div>
                                            <div className="clearfix"></div>
                                        </div>
                                    </div>
                                </div>


                            </div>
                        </div>


                        <div className="col-2 float-left reportNavigation">
                            <ul className="nav nav-tabs" id="myTab" role="tablist">
                                {/* General Info tab */}
                                <li className="nav-item col-12">
                                    <a
                                        onClick={() => {
                                            if (typeof get === "function") {
                                                get("eightdinfoService", "getEightDInfo", "EightDInfoList", "general");
                                            }
                                            setOpenSection("general");
                                        }}
                                        className={`nav-link ${openSection === "general" ? "active" : ""}`}
                                        id="general"
                                        data-toggle="tab"
                                        role="tab"
                                        aria-controls="home"
                                        aria-selected={openSection === "general"}
                                    >
                                        <span className="statusCirc"></span>Info
                                    </a>
                                </li>

                                {/* D1–D8 tabs */}
                                <li className="nav-item col-12">
                                    <a
                                        onClick={() => {
                                            fetchDData(1);
                                            setOpenSection("d1");
                                        }}
                                        className={`nav-link ${openSection === "d1" ? "active" : ""}`}
                                        id="d1"
                                    >
                                        <span className="verticalBar"></span>
                                        <span className="statusCirc"></span>D1
                                    </a>

                                </li>

                                <li className="nav-item col-12">
                                    <a
                                        onClick={() => {
                                            fetchDData(2);
                                            setOpenSection("d2");
                                        }}
                                        className={`nav-link ${openSection === "d2" ? "active" : ""}`}
                                        id="d2"
                                        data-toggle="tab"
                                        role="tab"
                                        aria-controls="contact"
                                        aria-selected={openSection === "d2"}
                                    >
                                        <span className="verticalBar"></span>
                                        <span className="statusCirc"></span>D2
                                    </a>
                                </li>

                                <li className="nav-item col-12">
                                    <a
                                        onClick={() => {
                                            fetchDData(3);
                                            setOpenSection("d3");
                                        }}
                                        className={`nav-link ${openSection === "d3" ? "active" : ""}`}
                                        id="d3"
                                        data-toggle="tab"
                                        role="tab"
                                        aria-controls="contact"
                                        aria-selected={openSection === "d3"}
                                    >
                                        <span className="verticalBar"></span>
                                        <span className="statusCirc"></span>D3
                                    </a>
                                </li>

                                <li className="nav-item col-12">
                                    <a
                                        onClick={() => {
                                            fetchDData(4);
                                            setOpenSection("d4");
                                        }}
                                        className={`nav-link ${openSection === "d4" ? "active" : ""}`}
                                        id="d4"
                                        data-toggle="tab"
                                        role="tab"
                                        aria-controls="contact"
                                        aria-selected={openSection === "d4"}
                                    >
                                        <span className="verticalBar"></span>
                                        <span className="statusCirc"></span>D4
                                    </a>
                                </li>

                                <li className="nav-item col-12">
                                    <a
                                        onClick={() => {
                                            fetchDData(5);
                                            setOpenSection("d5");
                                        }}
                                        className={`nav-link ${openSection === "d5" ? "active" : ""}`}
                                        id="d5"
                                        data-toggle="tab"
                                        role="tab"
                                        aria-controls="contact"
                                        aria-selected={openSection === "d5"}
                                    >
                                        <span className="verticalBar"></span>
                                        <span className="statusCirc"></span>D5
                                    </a>
                                </li>

                                <li className="nav-item col-12">
                                    <a
                                        onClick={() => {
                                            fetchDData(6);
                                            setOpenSection("d6");
                                        }}
                                        className={`nav-link ${openSection === "d6" ? "active" : ""}`}
                                        id="d6"
                                        data-toggle="tab"
                                        role="tab"
                                        aria-controls="contact"
                                        aria-selected={openSection === "d6"}
                                    >
                                        <span className="verticalBar"></span>
                                        <span className="statusCirc"></span>D6
                                    </a>
                                </li>

                                <li className="nav-item col-12">
                                    <a
                                        onClick={() => {
                                            fetchDData(7);
                                            setOpenSection("d7");
                                        }}
                                        className={`nav-link ${openSection === "d7" ? "active" : ""}`}
                                        id="d7"
                                        data-toggle="tab"
                                        role="tab"
                                        aria-controls="contact"
                                        aria-selected={openSection === "d7"}
                                    >
                                        <span className="verticalBar"></span>
                                        <span className="statusCirc"></span>D7
                                    </a>
                                </li>

                                <li className="nav-item col-12">
                                    <a
                                        onClick={() => {
                                            fetchDData(8);
                                            setOpenSection("d8");
                                        }}
                                        className={`nav-link ${openSection === "d8" ? "active" : ""}`}
                                        id="d8"
                                        data-toggle="tab"
                                        role="tab"
                                        aria-controls="contact"
                                        aria-selected={openSection === "d8"}
                                    >
                                        <span className="verticalBar"></span>
                                        <span className="statusCirc"></span>D8
                                    </a>
                                </li>
                            </ul>
                        </div>


                    </div>
                </div>
            </div>
        </Header >
    );
};

export default ReportsPage;

